import React from "react";
import {
  ThinkingWrapper,
  ProcessingRing,
  ProcessingShimmerText,
  ChecklistWrapper,
  ChecklistHeader,
  ChecklistTitle,
  ChecklistCount,
  ProgressTrack,
  ProgressFill,
  TaskList,
  TaskRow,
  TaskIconWrapper,
  TaskDot,
  TaskLabel,
  CheckCircle,
  CheckMark,
} from "./Styles";

export { ThinkingWrapper, ProcessingRing, ProcessingShimmerText } from "./Styles";
export { ProcessingRing as ThinkingRing, ProcessingShimmerText as ThinkingText } from "./Styles";

interface ProcessingMessageProps {
  text: string;
}

export const ProcessingMessage: React.FC<ProcessingMessageProps> = ({ text }) => (
  <ThinkingWrapper>
    <ProcessingRing />
    <ProcessingShimmerText>{text}</ProcessingShimmerText>
  </ThinkingWrapper>
);

export interface ProcessingTask {
  label: string;
  completed: boolean;
}

interface ProcessingChecklistProps {
  title: string;
  tasks: ProcessingTask[];
}

// Live checklist for a long, multi-step backend process: each task keeps the
// same rotating shimmer it always had while pending, and gets a mint checkmark
// (dimmed, no shimmer) once the backend confirms it's actually done. No task
// is singled out as "running right now" — several can complete in parallel,
// so the spinning ring at the top is the only "still working" signal; per-row
// state only shows what's verifiably true (done vs not yet).
export const ProcessingChecklist: React.FC<ProcessingChecklistProps> = ({ title, tasks }) => {
  const doneCount = tasks.filter((task) => task.completed).length;
  const total = tasks.length;

  return (
    <ChecklistWrapper>
      <ChecklistHeader>
        <ProcessingRing />
        <ChecklistTitle>{title}</ChecklistTitle>
        {total > 0 && (
          <ChecklistCount>
            <b>{doneCount}</b>/{total} completadas
          </ChecklistCount>
        )}
      </ChecklistHeader>

      {total > 0 && (
        <>
          <ProgressTrack>
            <ProgressFill $percent={(doneCount / total) * 100} />
          </ProgressTrack>
          <TaskList>
            {tasks.map((task, index) => (
              <TaskRow key={task.label}>
                <TaskIconWrapper>
                  {task.completed ? (
                    <svg viewBox="0 0 16 16" fill="none">
                      <CheckCircle cx={8} cy={8} r={7} strokeWidth={1.3} />
                      <CheckMark
                        d="M4.8 8.2l2.1 2.1 4.3-4.6"
                        strokeWidth={1.6}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <TaskDot />
                  )}
                </TaskIconWrapper>
                <TaskLabel $done={task.completed} $index={index}>
                  {task.label}
                </TaskLabel>
              </TaskRow>
            ))}
          </TaskList>
        </>
      )}
    </ChecklistWrapper>
  );
};
